// docs-api's routes, as the screen calls them on its own origin (request.js).
// Every id in a path is a uuid: fill the `{…_uuid}` placeholders with
// `.replace('{file_uuid}', uuid)`. Bodies and answers name references as
// `<thing>_uuid` (camelized `<thing>Uuid` by the shared client).

// ==========================================
// The install's platform routes (/api/v1), as in every module
// ==========================================
// Who is signed in: read by @zehnmindai/embed's SessionProvider.
export const AUTH_ME = '/api/v1/auth/me';
// Which sign-in the screen offers ({password}: the break-glass admin's form).
export const AUTH_SIGN_IN = '/api/v1/auth/sign-in';
// docs' scope catalogue and presets ({scopes: [{scope, category, description}], presets}).
export const AUTH_SCOPES = '/api/v1/auth/scopes';
// The install's people directory (?q=&limit=&offset=): [{uuid, person_id,
// display_name, email, kind, status}]; ?uuids=a,b (at most 200) names the
// people a read gives by uuid (owners, signers, authors), whatever their status.
export const PEOPLE = '/api/v1/users';
// Which language docs writes to me in ({language: en|ru|uz}).
export const MY_LANGUAGE = '/api/v1/users/me/language';

// ==========================================
// What the install allows, for the screens (anyone signed in)
// ==========================================
// {policies, upload: {max_file_size_mb, allowed_file_types}}
export const INSTALL_POLICIES = '/embed/v1/settings/policies';
// {can_manage, organization_name}: the console is open to the caller.
export const CONSOLE_CONTEXT = '/embed/v1/console/context';

// ==========================================
// E-IMZO: the signed-in person's certificates
// ==========================================
export const EIMZO_CREATE_CHALLENGE = '/embed/v1/auth/eimzo/challenge';
export const EIMZO_GET_CHALLENGE = '/embed/v1/auth/eimzo/challenge/{request_id}';
export const EIMZO_VERIFY = '/embed/v1/auth/eimzo/verify';
export const EIMZO_CERTIFICATES = '/embed/v1/auth/eimzo/certificates';

// ==========================================
// Nodes (files + folders share one model)
// ==========================================
export const NODE_CHILDREN = '/embed/v1/node/children';
export const SEARCH = '/embed/v1/search';
export const FILE_INDEX_STATUS = '/embed/v1/file/index-status';
export const NODE_TRASH = '/embed/v1/node/trash';
export const NODE_COUNTS = '/embed/v1/node/counts';
export const NODE_RESTORE_TRASH = '/embed/v1/node/{node_uuid}/restore-from-trash';
export const NODE_PURGE = '/embed/v1/node/{node_uuid}/purge';
export const NODE = '/embed/v1/node/{node_uuid}';
export const NODE_FOLDER_CREATE = '/embed/v1/node/folder';
export const NODE_BULK_MOVE = '/embed/v1/node/bulk-move';
export const NODE_BULK_DELETE = '/embed/v1/node/bulk-delete';
export const NODE_RESTORE = '/embed/v1/node/{node_uuid}/restore';
export const NODE_OWNER = '/embed/v1/node/{node_uuid}/owner';
export const NODE_RESTORE_TARGET = '/embed/v1/node/{node_uuid}/restore-target';
export const NODE_DELETE_PREVIEW = '/embed/v1/node/{node_uuid}/delete-preview';

// ==========================================
// Files
// ==========================================
// POST: upload (multipart).
export const FILES = '/embed/v1/file';
// GET (read), PATCH (rename/move), DELETE.
export const FILE = '/embed/v1/file/{file_uuid}';
// Every file the caller can read (?visibility=all_files|files&page&size&source&entity&ref):
// {items, total, page, size, pages}.
export const FILES_ALL = '/embed/v1/file/all';
export const FILES_BULK_COPY = '/embed/v1/file/bulk-copy';
export const FILES_ARCHIVED = '/embed/v1/file/archived';
export const FILE_ARCHIVE = '/embed/v1/file/{file_uuid}/archive';
export const FILE_RESTORE = '/embed/v1/file/{file_uuid}/restore';
export const FILE_RESTORE_TARGET = '/embed/v1/file/{file_uuid}/restore-target';
export const FILE_VISIBILITY = '/embed/v1/file/{file_uuid}/visibility';
export const FILE_ACTION_COUNT = '/embed/v1/file/action-count';
// The bytes, with the session cookie (never a token in the URL).
export const FILE_CONTENT = '/embed/v1/file/{file_uuid}/content';
export const FILE_VERSIONS = '/embed/v1/file/{file_uuid}/versions';
export const FILE_VERSION_UPLOAD = '/embed/v1/file/{file_uuid}/version';
export const FILE_VERSION = '/embed/v1/file/{file_uuid}/version/{version}';
export const FILE_VERSION_CONTENT = '/embed/v1/file/{file_uuid}/version/{version}/content';
export const FILE_VERSION_PDF = '/embed/v1/file/{file_uuid}/version/{version}/pdf';
export const FILE_VERSION_RESTORE = '/embed/v1/file/{file_uuid}/version/{version}/restore';
// PATCH: a version's label.
export const FILE_VERSION_RENAME = '/embed/v1/file/{file_uuid}/version/{version}';
// A person's lock on a file: PUT {reason, ref?} locks, DELETE unlocks.
export const FILE_LOCK = '/embed/v1/file/{file_uuid}/lock';
// Which of a template-made file's placeholders are still empty (read from the
// saved bytes) — what the signing policy `placeholders` checks.
export const FILE_PLACEHOLDERS_STATUS = '/embed/v1/file/{file_uuid}/placeholders/status';
export const FILE_CLONE = '/embed/v1/file/{file_uuid}/clone';
export const FILE_ACTIVITY = '/embed/v1/file/{file_uuid}/activity';
export const FILE_GENERATE_BLANK = '/embed/v1/file/generate/blank';

// Comments
export const FILE_COMMENTS = '/embed/v1/file/{file_uuid}/comments';
// PATCH updates, DELETE removes.
export const COMMENT = '/embed/v1/comments/{comment_uuid}';
export const COMMENT_RESOLVE = '/embed/v1/comments/{comment_uuid}/resolve';
export const COMMENT_UNRESOLVE = '/embed/v1/comments/{comment_uuid}/unresolve';

// ==========================================
// Sharing
// ==========================================
export const SHARE_FILE = '/embed/v1/share';
export const UNSHARE_FILE = '/embed/v1/unshare';
// The people directory for pickers (?search=&limit=&offset=; ?uuids= by docs'
// uuid; ?person_ids= maps zehn-id person ids to docs' uuids).
export const SHARE_USERS = '/embed/v1/share/all-users';
export const SHARED_USERS_LIST = '/embed/v1/share/shared-users';
// Who has access to a node and how, with the caller's own access and the
// install's sharing policy (the share dialog's one read).
export const NODE_SHARES = '/embed/v1/share/node/{node_uuid}';
// One share: PATCH its options, DELETE it.
export const SHARE_ITEM = '/embed/v1/share/{share_uuid}';
export const RECEIVED_SHARES = '/embed/v1/share/received';
export const SENT_SHARES = '/embed/v1/share/sent';

// ==========================================
// Signing: E-IMZO (prepare → sign in the browser → timestamp → complete)
// ==========================================
export const EIMZO_SIGN_PREPARE = '/embed/v1/sign/eimzo/prepare';
export const EIMZO_SIGN_TIMESTAMP = '/embed/v1/sign/eimzo/timestamp';
export const EIMZO_SIGN_COMPLETE = '/embed/v1/sign/eimzo/complete';
export const EIMZO_SIGN_SIGNATURES = '/embed/v1/sign/eimzo/files/{file_uuid}/signature';
export const EIMZO_SIGN_REVERIFY = '/embed/v1/sign/eimzo/files/{file_uuid}/reverify';

// Unified workflow (agreement approval + E-IMZO signer setup).
// participants: POST adds, DELETE removes, PATCH /{participant_uuid} updates.
export const WORKFLOW = '/embed/v1/workflow/file/{file_uuid}';
export const WORKFLOW_PARTICIPANTS = '/embed/v1/workflow/file/{file_uuid}/participants';
export const WORKFLOW_PARTICIPANT = '/embed/v1/workflow/file/{file_uuid}/participants/{participant_uuid}';
export const WORKFLOW_RESPOND = '/embed/v1/workflow/file/{file_uuid}/respond';
export const WORKFLOW_DECLINE = '/embed/v1/workflow/file/{file_uuid}/decline';
export const WORKFLOW_WITHDRAW = '/embed/v1/workflow/file/{file_uuid}/withdraw';
// Signing rendition + verification artefacts (binary — fetch as blobs).
export const WORKFLOW_RENDITION = '/embed/v1/workflow/file/{file_uuid}/rendition';
export const WORKFLOW_QR = '/embed/v1/workflow/file/{file_uuid}/qr.png';
export const WORKFLOW_PKCS7 = '/embed/v1/workflow/file/{file_uuid}/pkcs7';
export const WORKFLOW_SIGNATURE_SHEET = '/embed/v1/workflow/file/{file_uuid}/signature-sheet';
export const WORKFLOW_VERIFICATION_VIEWS = '/embed/v1/workflow/file/{file_uuid}/verification-views';
// Picker support: the install's people + their registered E-IMZO certs.
export const WORKFLOW_USERS_WITH_CERTS = '/embed/v1/workflow/users-with-certs';

// Atomic document-type enrollment (one-way: ordinary -> agreement | signed)
export const ENROLL_SIGNED = '/embed/v1/file/{file_uuid}/document-type/signed';
export const ENROLL_AGREEMENT = '/embed/v1/file/{file_uuid}/document-type/agreement';

// Public verification page data (no sign-in; a guest signer's token unlocks more).
export const VERIFY = '/verify/{code}';

// ==========================================
// Guest links (public link sharing)
// ==========================================
export const GUEST_SHARE_LINK = '/embed/v1/share/guest-share/link';
export const GUEST_SHARE_RESOURCE = '/guest-share/resource';
// A folder guest link: what is inside a folder it reaches, and one node.
export const GUEST_SHARE_CHILDREN = '/guest-share/children';
export const GUEST_SHARE_NODE = '/guest-share/node';
// A node's guest links: GET ?node_uuid=, POST to make one; /{link_uuid}: PATCH, DELETE.
export const GUEST_LINKS = '/embed/v1/share/guest-links';
export const GUEST_LINK = '/embed/v1/share/guest-links/{link_uuid}';

// ==========================================
// Guest signers: E-IMZO sign-in for people without a zehn-id account
// ==========================================
export const GUEST_AUTH_CHALLENGE = '/guest-auth/challenge';
export const GUEST_AUTH_VERIFY = '/guest-auth/verify';

// ==========================================
// WOPI / Collabora Online
// ==========================================
export const WOPI_TOKEN = '/embed/v1/wopi/token/{file_uuid}';
export const WOPI_TOKEN_FROM_SHARE = '/embed/v1/wopi/token/from-share';
export const WOPI_DISCOVERY = '/embed/v1/wopi/discovery';

// ==========================================
// Template library (install-wide, never in the folder tree)
// ==========================================
export const TEMPLATES = '/embed/v1/templates';
export const TEMPLATE = '/embed/v1/templates/{template_uuid}';
export const TEMPLATE_BLANK = '/embed/v1/templates/blank';
export const TEMPLATE_UPLOAD = '/embed/v1/templates/upload';
// Private copy of a template into my templates (placeholders come along).
export const TEMPLATE_DUPLICATE = '/embed/v1/templates/{template_uuid}/duplicate';
// Create a document from a template (clone + placeholder definitions copied).
export const TEMPLATE_INSTANTIATE = '/embed/v1/templates/{template_uuid}/instantiate';
// A template's (or document's) placeholder rows, with the record it is about.
export const TEMPLATE_PLACEHOLDERS = '/embed/v1/file/{file_uuid}/placeholders';
// Modules whose records may fill templates, and where docs reads them for the
// screen: its own pass-through (`/pass/<module>`), as the person.
export const TEMPLATE_SOURCES = '/embed/v1/template-sources';

// ==========================================
// Capability grants to zehn-aip ("Allow the assistant"; docs-api
// ZEHN_GRANTS=on, 404 while off): POST issues, GET lists
// (?object=folder:<uuid>|file:<uuid>&grantee=zehn-aip), DELETE /{id} revokes.
// ==========================================
export const GRANTS = '/embed/v1/grants';
export const GRANT = '/embed/v1/grants/{id}';

// ==========================================
// Intent results: docs.sign / docs.pick, signed for the invoker
// ==========================================
export const INTENT_RESULT = '/embed/v1/intents/{intent}/result';
